import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import { ISurveyQuestion } from "../../../../common/data/dtos/survey-question";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import surveyQuestionService from "../../../../services/survey-question-service";

type SurveyQuestionFormValues = {
    id?: string;
    title: string;
};

interface Props {
    isModalOpened: boolean;
    selectedSurvey: ISurvey | null;
    selectedSurveyQuestion: ISurveyQuestion | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const SurveyQuestionModal = ({ isModalOpened, selectedSurvey, selectedSurveyQuestion, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            title: '',
        },

        validate: (values) => {
            const errors: {
                title?: string;
            } = {};

            if (!values.title)
                errors.title = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {
        resetForm();

        if (selectedSurveyQuestion)
            setFieldValues(selectedSurveyQuestion);

    }, [isModalOpened]);

    const onFormSubmit = async (values: SurveyQuestionFormValues) => {
        if (selectedSurveyQuestion) {

            if (!selectedSurvey) {
                showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
                return;
            }

            setIsSubmitting(true);

            const response = await surveyQuestionService.updateSurveyQuestion({
                id: selectedSurveyQuestion.id,
                title: values.title,
                surveyId: selectedSurvey.id!,
                rowVersion: selectedSurveyQuestion.rowVersion,
                status: selectedSurveyQuestion.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await surveyQuestionService.createSurveyQuestion({
                title: values.title,
                surveyId: selectedSurvey?.id,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                title: '',
            },
        });
    };

    const setFieldValues = (values: ISurveyQuestion) => {
        formik.setFieldValue('title', values.title);
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='survey-question-modal'>Manutenção de questões</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados da questão' icon='ViewList'>
                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='title' label='Título' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.title}
                                        invalidFeedback={formik.errors.title}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};